import React from 'react';

interface SecuritySettings {
  sessionTimeout: number;
  passwordMinLength: number;
  requirePasswordChange: boolean;
  passwordChangeFrequency: number;
  enable2FA: boolean;
}

interface SecurityTabProps {
  settings: SecuritySettings;
  onUpdate: (patch: Partial<SecuritySettings>) => void;
}

const SecurityTab: React.FC<SecurityTabProps> = ({ settings, onUpdate }) => (
  <div className="space-y-6">
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
      <h3 className="font-semibold text-blue-900 mb-2">Security Settings</h3>
      <p className="text-sm text-blue-800">Configure security and authentication settings</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
        <input
          type="number"
          value={settings.sessionTimeout}
          onChange={(e) => onUpdate({ sessionTimeout: parseInt(e.target.value) })}
          min="5" max="1440"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-500 mt-1">Users will be logged out after this period of inactivity</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Password Minimum Length</label>
        <input
          type="number"
          value={settings.passwordMinLength}
          onChange={(e) => onUpdate({ passwordMinLength: parseInt(e.target.value) })}
          min="6" max="32"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>

    <div className="space-y-4 pt-4">
      <div className="flex items-center">
        <input type="checkbox" checked={settings.requirePasswordChange}
          onChange={(e) => onUpdate({ requirePasswordChange: e.target.checked })}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
        <label className="ml-2 block text-sm text-gray-900">Require Periodic Password Change</label>
      </div>
      {settings.requirePasswordChange && (
        <div className="ml-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Password Change Frequency (days)</label>
          <input
            type="number"
            value={settings.passwordChangeFrequency}
            onChange={(e) => onUpdate({ passwordChangeFrequency: parseInt(e.target.value) })}
            min="30" max="365"
            className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}
      <div className="flex items-center">
        <input type="checkbox" checked={settings.enable2FA}
          onChange={(e) => onUpdate({ enable2FA: e.target.checked })}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
        <label className="ml-2 block text-sm text-gray-900">Enable Two-Factor Authentication (2FA)</label>
      </div>
    </div>
  </div>
);

export default SecurityTab;
